import StatisticCard from "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/StatisticCard";
import React, { CSSProperties, useContext } from "react";
import { BotType, LanguageNameSpaces } from "@shared/types";
import Text from "common/Typography/Text";
import { getDisplayedTimeframe } from "@shared/features/bots/utils";
import MarginTypeChart, {
  MarginTypeGauge,
} from "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/MarginTypeChart";
import { toFixed } from "@shared/utils/numbers";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Title from "common/Typography/Title";
import styled, { useTheme } from "styled-components";
import { getDaysDiff, getDisplayTimeDuration } from "@shared/utils/date";
import MostTradedCoinsStatistic from "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/MostTradedCoinsStatistic";
import TargetsStatistic from "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/TargetsStatistic";
import PnlStatusStatistic from "features/bots/BotForm/Backtesting/ExtraDetailsDrawer/PnlStatusStatistic";
import { PERCENT_PRECISION } from "@shared/features/tradingTerminal/common/constants";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH, PAGE_SPINNER_MARGIN_FROM_TOP } from "common/constants";
import InProgressMessage from "features/bots/BotForm/Backtesting/common/InProgressMessage";
import {
  AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS,
  BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS,
} from "@shared/features/bots/BotForm/common/constants";
import { BacktestDetailsContext } from "@shared/features/bots/BacktestDetails.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import NewLookBacktestStatistics from "features/bots/BotForm/Backtesting/NewLook/BacktestStatistics";

interface BacktestingStatisticsProps {
  loading?: boolean;
  isExtension?: boolean;
  inProgress?: boolean;
  isGroupDrawer?: boolean;
  style?: CSSProperties;
}

const BacktestingStatistics = ({
  loading,
  isExtension,
  inProgress,
  isGroupDrawer,
  style,
}: BacktestingStatisticsProps) => {
  const theme = useTheme();
  const { liveBacktestingData: backtestingRun, botData } = useContext(
    BacktestDetailsContext
  )!;
  const showMostTradedCoins = botData?.bot_type === BotType.SIGNAL;
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  // New look (backtest drawer): the statistics strip and panels. The
  // marketplace's group drawer keeps its own layout.
  if (theme.isNewLook && !isGroupDrawer) {
    return (
      <NewLookExtension
        $isExtension={isExtension}
        $isMobile={isMobile}
        style={style}
      >
        {isExtension && inProgress && <InProgressMessage />}
        <NewLookBacktestStatistics narrow={isExtension} loading={loading} />
      </NewLookExtension>
    );
  }

  const statistics = (
    <StatisticsWrapper
      $isMobile={isMobile}
      $isGroupDrawer={isGroupDrawer}
      $showMostTradedCoins={showMostTradedCoins}
    >
      {!isGroupDrawer && (
        <StatisticCard
          title={t("backtesting.statistics.time_range")}
          value={getDisplayedTimeframe(backtestingRun?.backtesting_time_range)}
          extraComponent={
            <Text>
              {getDaysDiff(
                backtestingRun?.backtesting_time_range?.start,
                backtestingRun?.backtesting_time_range?.end
              )}{" "}
              {t("backtesting.labels.days")}
            </Text>
          }
          gridArea="TimeRange"
        />
      )}
      <StatisticCard
        title={t("backtesting.statistics.leverage")}
        value={
          backtestingRun?.avg_multiplier
            ? `${t("backtesting.labels.avg")} ${toFixed(
                backtestingRun?.avg_multiplier,
                1
              )}x`
            : "-"
        }
        extraComponent={
          <MarginTypeChart data={backtestingRun?.margin_type_to_counter} />
        }
        aside={
          theme.isNewLook ? (
            <MarginTypeGauge data={backtestingRun?.margin_type_to_counter} />
          ) : undefined
        }
        gridArea="Leverage"
      />
      <StatisticCard
        title={t("backtesting.statistics.max_drawdown")}
        value={
          backtestingRun?.max_drawdown
            ? `${toFixed(
                backtestingRun?.max_drawdown * 100,
                PERCENT_PRECISION
              )}%`
            : "-"
        }
        helperProps={{
          ...BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS,
          description: t(BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS.description),
          title: t(BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS.title),
        }}
        gridArea="MaxDrawdown"
      />
      <StatisticCard
        title={t("backtesting.statistics.avg_trade_duration")}
        value={getDisplayTimeDuration(backtestingRun?.avg_trade_time) ?? "-"}
        helperProps={{
          ...AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS,
          description: t(AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS.description),
          title: t(AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS.title),
        }}
        gridArea="AvgTradeDuration"
      />
      {showMostTradedCoins && <MostTradedCoinsStatistic />}
      <TargetsStatistic data={backtestingRun} />
      <PnlStatusStatistic />
    </StatisticsWrapper>
  );

  return (
    <Wrapper $isExtension={isExtension} $isMobile={isMobile} style={style}>
      <LoadingSpinnerOverlay
        isLoading={loading}
        spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
        wrap={true}
        wrapStyle={{ height: "auto" }}
        hideContentWhileLoading={true}
        backgroundColor={theme.colors.statisticsBgColor}
      >
        {isExtension && (
          <>
            {inProgress && <InProgressMessage />}
            {theme.isNewLook ? (
              <NewLookTitle>
                {t("backtesting.statistics.statistics_title")}
              </NewLookTitle>
            ) : (
              <Title level={2} style={{ marginBottom: 16 }}>
                {t("backtesting.statistics.statistics_title")}
              </Title>
            )}
          </>
        )}
        {statistics}
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default BacktestingStatistics;

const Wrapper = styled.div<{ $isExtension?: boolean; $isMobile?: boolean }>`
  height: 100%;
  overflow-y: auto;
  padding: ${(p) =>
    p.$isExtension
      ? "20px"
      : p.$isMobile
      ? "12px 16px 24px"
      : "12px 24px 24px"};
  &::-webkit-scrollbar {
    display: none;
  }
  ${(p) =>
    p.$isExtension &&
    `
    background: ${p.theme.colors.statisticsBgColor};
  `};
  ${(p) =>
    p.theme.isNewLook &&
    p.$isExtension &&
    `
    padding: 24px 20px;
    background: ${p.theme.colors.cardLightBgColor};
  `};
`;
const NewLookTitle = styled.div`
  margin-bottom: 14px;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 16px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const StatisticsWrapper = styled.div<{
  $isMobile?: boolean;
  $isGroupDrawer?: boolean;
  $showMostTradedCoins?: boolean;
}>`
  display: grid;
  grid-gap: ${(p) => (p.$isMobile ? 12 : p.theme.isNewLook ? 12 : 16)}px;
  grid-template-rows: auto;
  grid-template-columns: 1fr 1fr;
  grid-template-areas: ${(p) =>
    getGridTemplateAreas(p.$isGroupDrawer, p.$showMostTradedCoins)};
`;

const getGridTemplateAreas = (
  isGroupDrawer?: boolean,
  showMostTradedCoins?: boolean
) => {
  let templateAreas = "";
  if (!isGroupDrawer) {
    templateAreas = `"TimeRange TimeRange"`;
  }
  templateAreas = templateAreas.concat(
    ` "Leverage Leverage"
    "MaxDrawdown AvgTradeDuration"`
  );
  if (showMostTradedCoins) {
    templateAreas = templateAreas.concat(
      ` "MostTradedCoins MostTradedCoins" "MostTradedCoins MostTradedCoins"`
    );
  }
  templateAreas = templateAreas.concat(
    ` "Targets Targets"
    "Targets Targets"
    "PnlStatus PnlStatus"
    "PnlStatus PnlStatus"`
  );
  return templateAreas;
};

const NewLookExtension = styled.div<{
  $isExtension?: boolean;
  $isMobile?: boolean;
}>`
  ${(p) =>
    p.$isExtension
      ? `
    height: 100%;
    overflow-y: auto;
    padding: 24px 20px;
    border-right: 1px solid ${getNewLookTokens(p.theme).hairline};
    background: ${getNewLookTokens(p.theme).canvas};
  `
      : `
    padding: 0 ${p.$isMobile ? 16 : 24}px 24px;
  `}
`;
